import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie, Settings2 } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const KEY = 'kitxens_cookie_consent_v2';

const defaultPrefs = {
  necessary: true,
  functional: true,
  analytics: false,
  marketing: false,
  set: false,
};

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);
  const { pathname } = useLocation();
  const internal = pathname.startsWith('/admin') || pathname.startsWith('/affiliates/dashboard') || pathname === '/login';

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(KEY) || 'null');
      if (!stored || !stored.set) {
        setVisible(true);
      } else {
        setPrefs(stored);
      }
    } catch (e) {
      setVisible(true);
    }
    const handler = () => {
      setShowPrefs(true);
      setVisible(true);
    };
    window.addEventListener('kitxens:open-cookie-prefs', handler);
    return () => window.removeEventListener('kitxens:open-cookie-prefs', handler);
  }, []);

  const save = useCallback((next) => {
    const final = { ...next, necessary: true, set: true };
    localStorage.setItem(KEY, JSON.stringify(final));
    setPrefs(final);
    setVisible(false);
    setShowPrefs(false);
    // Consent Mode v2: tell Google the user's choice — without this signal the
    // Google Tag stays silent forever and GA4 receives no data.
    try {
      const consent = {
        analytics_storage: final.analytics ? 'granted' : 'denied',
        ad_storage: final.marketing ? 'granted' : 'denied',
        ad_user_data: final.marketing ? 'granted' : 'denied',
        ad_personalization: final.marketing ? 'granted' : 'denied',
      };
      if (window.gtag) window.gtag('consent', 'update', consent);
      // Microsoft Clarity mirrors the analytics choice (cookieless when denied).
      if (window.clarity) window.clarity('consent', !!final.analytics);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'cookie_consent_update', consent_analytics: final.analytics, consent_marketing: final.marketing });
    } catch (e) { /* noop */ }
    // Signal the rest of the app (e.g. AIConcierge) that the consent
    // surface is gone and other floating elements can take its place.
    try { window.dispatchEvent(new CustomEvent('kitxens:cookie-consent-set')); } catch (e) { /* noop */ }
  }, []);

  const acceptAll = () => save({ necessary: true, functional: true, analytics: true, marketing: true });
  const rejectAll = () => save({ necessary: true, functional: false, analytics: false, marketing: false });

  if (!visible || internal) return null;

  return (
    <AnimatePresence>
      <motion.div
        key="consent-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`fixed inset-0 z-[60] kx-cookie-layer ${showPrefs ? 'bg-black/30 backdrop-blur-sm' : 'pointer-events-none'}`}
        onClick={() => showPrefs && setShowPrefs(false)}
      />
      {showPrefs ? (
        <motion.div
          key="prefs"
          initial={{ opacity: 0, y: 20, scale: 0.98 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.98 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          className="kx-cookie-layer fixed inset-x-4 bottom-4 lg:inset-auto lg:bottom-6 lg:right-6 lg:max-w-md z-[70] bg-white rounded-2xl shadow-2xl border border-neutral-200 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center gap-2 px-5 py-4 border-b border-neutral-100">
            <Settings2 className="w-4 h-4 text-[#F77F11]" />
            <h3 className="font-semibold text-neutral-900">Cookie preferences</h3>
            <button onClick={() => setShowPrefs(false)} aria-label="Close" className="ml-auto text-neutral-500 hover:text-neutral-800">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-5 space-y-4 text-sm text-neutral-700">
            <p>Choose which cookies you want to allow. You can change this any time from our Cookie Policy.</p>
            {[
              { key: 'necessary', title: 'Necessary', desc: 'Required for the site to work. Cannot be disabled.', disabled: true },
              { key: 'functional', title: 'Functional', desc: 'Remembers language and preferences for a better experience.' },
              { key: 'analytics', title: 'Analytics', desc: 'Helps us understand site usage to keep improving.' },
              { key: 'marketing', title: 'Marketing', desc: 'Used to measure and tailor relevant content and campaigns.' },
            ].map((c) => (
              <label key={c.key} className="flex items-start gap-3 p-3 rounded-xl border border-neutral-200 hover:border-[#F77F11] transition-colors">
                <input
                  type="checkbox"
                  checked={!!prefs[c.key]}
                  disabled={c.disabled}
                  onChange={(e) => setPrefs((p) => ({ ...p, [c.key]: e.target.checked }))}
                  className="mt-1 accent-[#F77F11]"
                />
                <div>
                  <p className="font-semibold text-neutral-900">{c.title}</p>
                  <p className="text-xs text-neutral-500 mt-0.5">{c.desc}</p>
                </div>
              </label>
            ))}
          </div>
          <div className="p-4 border-t border-neutral-100 flex flex-wrap gap-2 justify-end">
            <button onClick={rejectAll} className="px-4 h-10 rounded-full text-sm font-semibold border border-neutral-300 hover:bg-neutral-50" data-testid="cookie-prefs-reject">
              Reject non-essential
            </button>
            <button onClick={() => save(prefs)} className="px-4 h-10 rounded-full text-sm font-semibold bg-neutral-900 text-white hover:bg-neutral-800" data-testid="cookie-prefs-save">
              Save preferences
            </button>
            <button onClick={acceptAll} className="px-4 h-10 rounded-full text-sm font-semibold bg-[#F77F11] hover:bg-[#d8651a] text-white" data-testid="cookie-prefs-accept">
              Accept all
            </button>
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="bar"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="kx-cookie-layer fixed inset-x-0 bottom-0 z-[70] bg-white/95 backdrop-blur border-t border-neutral-200 shadow-[0_-8px_30px_-15px_rgba(0,0,0,0.25)] pointer-events-auto"
        >
          <div className="max-w-[1180px] mx-auto px-4 sm:px-6 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-2">
            <p className="flex-1 min-w-[200px] text-[11.5px] sm:text-[12.5px] text-neutral-600 leading-snug">
              <Cookie className="inline w-3.5 h-3.5 text-[#F77F11] mr-1.5 -mt-0.5" />
              We use cookies to make the site work and improve your experience.{' '}
              <Link to="/cookies" className="underline text-[#F77F11]">Cookie Policy</Link>
            </p>
            <div className="flex items-center gap-1.5 ml-auto">
              <button
                onClick={rejectAll}
                className="px-3 h-8 rounded-full text-[12px] font-semibold text-neutral-600 hover:bg-neutral-100 transition-colors"
                data-testid="cookie-reject"
              >
                Reject
              </button>
              <button
                onClick={() => setShowPrefs(true)}
                className="px-3 h-8 rounded-full text-[12px] font-semibold text-neutral-600 hover:bg-neutral-100 transition-colors"
                data-testid="cookie-customize"
              >
                Customize
              </button>
              <button
                onClick={acceptAll}
                className="px-4 h-8 rounded-full text-[12px] font-bold bg-[#F77F11] hover:bg-[#d8651a] text-white transition-colors"
                data-testid="cookie-accept-all"
              >
                Accept all
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
